import React, { useState, useEffect } from 'react';
import { Tags, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from './ui/dialog';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const PALETTE = ['#6366f1', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444'];

export function WorkstreamManager({ boardId, workstreams = [], onChange }) {
  const { authedFetch } = useAuth();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Seed only when the dialog opens, so a parent refresh during editing
    // doesn't clobber in-flight (unsaved) row edits.
    if (open) {
      setRows(workstreams.map((w) => ({ ...w, _dirty: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateRow = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch, _dirty: true } : r)));
  };

  const handleSaveRow = async (row) => {
    if (!row._dirty) return;
    try {
      await authedFetch(`/api/workstreams/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: row.name, color: row.color }),
      });
      updateRow(row.id, { _dirty: false });
      onChange?.();
    } catch (e) {
      toast.error('Failed to save category');
    }
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const color = PALETTE[rows.length % PALETTE.length];
      const created = await authedFetch(`/api/boards/${boardId}/workstreams`, {
        method: 'POST',
        body: JSON.stringify({ name: `Category ${rows.length + 1}`, color }),
      });
      setRows((rs) => [...rs, { ...created, _dirty: false }]);
      onChange?.();
      toast.success('Category added');
    } catch (e) {
      toast.error('Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Projects in it will be kept but uncategorised.')) return;
    try {
      await authedFetch(`/api/workstreams/${id}`, { method: 'DELETE' });
      setRows((rs) => rs.filter((r) => r.id !== id));
      onChange?.();
      toast.success('Category deleted');
    } catch (e) {
      toast.error('Failed to delete category');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="manage-categories-button">
          <Tags className="h-3.5 w-3.5" />
          Categories
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="manage-categories-dialog">
        <DialogHeader>
          <DialogTitle>Manage Categories</DialogTitle>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto py-1">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No categories yet. Add one to group your projects.
            </p>
          )}
          {rows.map((row) => (
            <div key={row.id} className="flex items-center gap-2" data-testid={`category-row-${row.id}`}>
              <input
                type="color"
                value={row.color}
                onChange={(e) => updateRow(row.id, { color: e.target.value })}
                onBlur={() => handleSaveRow(row)}
                className="h-8 w-8 shrink-0 cursor-pointer rounded border bg-transparent p-0.5"
                data-testid={`category-color-${row.id}`}
                aria-label="Category color"
              />
              <Input
                value={row.name}
                onChange={(e) => updateRow(row.id, { name: e.target.value })}
                onBlur={() => handleSaveRow(row)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveRow(row); } }}
                className="h-8 text-sm"
                data-testid={`category-name-${row.id}`}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                onClick={() => handleDelete(row.id)}
                data-testid={`category-delete-${row.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleAdd}
            disabled={saving}
            data-testid="add-category-button"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add Category
          </Button>
          <Button size="sm" onClick={() => setOpen(false)} data-testid="categories-done-button">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
